import { CdkDrag, CdkDragEnd, CdkDragRelease } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, ContentChild, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import _ from 'lodash';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-custom-floating-component',
  standalone: true,
  imports: [CommonModule, CdkDrag, ButtonModule],
  templateUrl: './custom-floating-component.component.html',
  styleUrl: './custom-floating-component.component.scss'
})
export class CustomFloatingComponentComponent<T> implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: T | undefined;
  /** Emitted whenever a drag gesture ends and the bubble snaps to a corner. */
  @Output() dragReleased = new EventEmitter<void>();
  @ContentChild('floatingUI') floatingUI!: TemplateRef<{ $implicit: T }>;
  @ViewChild('draggableButton', { static: true }) draggableButton!: ElementRef<HTMLElement>;
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLElement>;
  private containerWidth: number = 0;
  private containerHeight: number = 0;
  private margin = 20;
  private resizeObserver?: ResizeObserver;
  private pollingInterval?: number;
  private lastCheckTime: number = 0;
  private readonly POLLING_THROTTLE_MS = 250;
  isDragging: boolean = false;
  dragPosition = { x: 0, y: 0 };

  ngOnInit() {
    // Initial setup
  }

  ngAfterViewInit() {
    this.updateContainerDimensions();
    this.setupResizeObserver();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.pollingInterval) {
      window.cancelAnimationFrame(this.pollingInterval);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateContainerDimensions();
    this.snapToNearestCorner(0, 0);
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this.container) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          const newHeight = entry.contentRect.height;

          if (newWidth !== this.containerWidth || newHeight !== this.containerHeight) {
            this.updateContainerDimensions();
            this.snapToNearestCorner(0, 0);
          }
        }
      });

      this.resizeObserver.observe(this.container.nativeElement);
    } else {
      console.warn('ResizeObserver not supported, using optimized polling fallback');
      this.setupOptimizedPollingFallback();
    }
  }

  private setupOptimizedPollingFallback(): void {
    const checkSize = () => {
      const now = performance.now();

      if (now - this.lastCheckTime >= this.POLLING_THROTTLE_MS) {
        if (this.container) {
          const newWidth = this.container.nativeElement.offsetWidth;
          const newHeight = this.container.nativeElement.offsetHeight;

          if (newWidth !== this.containerWidth || newHeight !== this.containerHeight) {
            this.containerWidth = newWidth;
            this.containerHeight = newHeight;
            this.snapToNearestCorner(0, 0);
          }
        }
        this.lastCheckTime = now;
      }

      if (this.container) {
        this.pollingInterval = window.requestAnimationFrame(checkSize);
      }
    };

    this.pollingInterval = window.requestAnimationFrame(checkSize);
  }

  private updateContainerDimensions() {
    if (this.container) {
      const rect = this.container.nativeElement.getBoundingClientRect();
      this.containerWidth = rect.width;
      this.containerHeight = rect.height;
    }
  }

  onDragStarted(event: any): void {
    this.isDragging = true;
    event.source.element.nativeElement.classList.add('dragging');
    event.source.setFreeDragPosition({ x: this.dragPosition.x, y: this.dragPosition.y });
  }

  onDragReleased(event: CdkDragEnd | CdkDragRelease): void {
    this.isDragging = false;
    this.dragReleased.emit();
    event.source.element.nativeElement.classList.remove('dragging');
    const element = event.source.element.nativeElement;
    const rect = element.getBoundingClientRect();
    const containerRect = this.container.nativeElement.getBoundingClientRect();

    // Calculate position relative to container
    const x = rect.left - containerRect.left;
    const y = rect.top - containerRect.top;

    const buttonWidth = element.offsetWidth;
    const buttonHeight = element.offsetHeight;

    const initialLeft = this.containerWidth - buttonWidth - this.margin;
    const initialTop = this.containerHeight - buttonHeight - this.margin;

    const offsetX = x - initialLeft;
    const offsetY = y - initialTop;

    this.snapToNearestCorner(offsetX, offsetY);
  }

  private snapToNearestCorner(xOffset: number, yOffset: number): void {
    const buttonElement = this.draggableButton.nativeElement;
    if (!buttonElement) return;

    const buttonWidth = buttonElement.offsetWidth;
    const buttonHeight = buttonElement.offsetHeight;

    const currentLeft = (this.containerWidth - buttonWidth - this.margin) + xOffset;
    const currentTop = (this.containerHeight - buttonHeight - this.margin) + yOffset;

    const corners = [
      { x: this.margin, y: this.margin, name: 'top-left' },
      { x: this.containerWidth - buttonWidth - this.margin, y: this.margin, name: 'top-right' },
      { x: this.margin, y: this.containerHeight - buttonHeight - this.margin, name: 'bottom-left' },
      { x: this.containerWidth - buttonWidth - this.margin, y: this.containerHeight - buttonHeight - this.margin, name: 'bottom-right' }
    ];

    let nearestCorner = corners[0];
    let minDistance = Infinity;

    for (const corner of corners) {
      const distance = Math.sqrt(
        Math.pow(currentLeft - corner.x, 2) + Math.pow(currentTop - corner.y, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearestCorner = corner;
      }
    }

    const newX = nearestCorner.x - (this.containerWidth - buttonWidth - this.margin);
    const newY = nearestCorner.y - (this.containerHeight - buttonHeight - this.margin);

    this.dragPosition.x = newX;
    this.dragPosition.y = newY;
    this.dragPosition = _.cloneDeep(this.dragPosition);
  }

  getButtonElement(): HTMLElement {
    return this.draggableButton.nativeElement;
  }
}
